import React, { useState, useEffect, useRef } from 'react';
import { searchProductos } from '../services/productos';
import { registrarOperacion } from '../services/historial';
import { Producto, PaginationInfo } from '../types';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Pagination';
import '../styles/POS.css';
import '../styles/ProductPage.css';

interface CartItem extends Producto {
    quantity: number;
}

const POS: React.FC = () => {
    const [products, setProducts] = useState<Producto[]>([]);
    const [cart, setCart] = useState<CartItem[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pagination, setPagination] = useState<Partial<PaginationInfo>>({});
    const [searchKey, setSearchKey] = useState<number>(0);

    const isSearching = !!searchQuery.trim();

    // Scanner buffer
    const scannerBuffer = useRef<string>('');
    const lastKeyTime = useRef<number>(0);

    // Initial load - REMOVED: Do not load default products
    /*
    useEffect(() => {
        loadProducts();
    }, []);
    */

    // Scanner Listener
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const currentTime = Date.now();

            // If time between keys is long, reset buffer (it's manual typing)
            if (currentTime - lastKeyTime.current > 100) {
                scannerBuffer.current = '';
            }
            lastKeyTime.current = currentTime;

            if (e.key === 'Enter') {
                if (scannerBuffer.current.length > 5) { // Threshold for valid barcode
                    handleScan(scannerBuffer.current);
                    scannerBuffer.current = '';
                    e.preventDefault(); // Prevent default enter behavior if it was a scan
                }
            } else if (e.key.length === 1) { // Printable chars
                scannerBuffer.current += e.key;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products]);

    // removed loadProducts

    const handleScan = async (code: string) => {
        // First check locally in current list
        const found = products.find(p => p.codigo_barras === code || p.referencia === code);
        if (found) {
            addToCart(found);
            return;
        }

        // Check DB
        try {
            const res = await searchProductos(code, 1, 1);
            if (res.data && res.data.length > 0 && res.data[0]) {
                addToCart(res.data[0]);
            } else {
                alert(`Producto no encontrado: ${code}`);
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        const fetchSearchResults = async () => {
            if (!isSearching) {
                setProducts([]);
                setPagination({});
                return;
            }
            setLoading(true);
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const res: any = await searchProductos(searchQuery, currentPage, 10);
                const matches = res.productos || res.data || [];
                if (matches.length > 0) {
                    setProducts(matches);
                    setPagination(res.pagination || {});
                } else {
                    setProducts([]);
                    setPagination({});
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchSearchResults();
    }, [searchQuery, currentPage, isSearching]);

    const handleSearch = (query: string) => {
        setSearchQuery(query);
        setCurrentPage(1);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setProducts([]);
        setPagination({});
        setCurrentPage(1);
        setSearchKey(prev => prev + 1);
    };

    const addToCart = (product: Producto) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                return prev.map(item =>
                    item.id === product.id
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prev, { ...product, quantity: 1 }];
        });
    };

    const removeFromCart = (id: number) => {
        setCart(prev => prev.filter(item => item.id !== id));
    };

    const updateQuantity = (id: number, delta: number) => {
        setCart(prev => prev.map(item => {
            if (item.id === id) {
                const newQty = item.quantity + delta;
                return newQty > 0 ? { ...item, quantity: newQty } : item;
            }
            return item;
        }));
    };

    const calculateTotal = () => {
        return cart.reduce((sum, item) => sum + (item.precio_venta * item.quantity), 0);
    };

    const handleCheckout = async () => {
        if (cart.length === 0) return;
        setProcessing(true);
        try {
            // Prepare payload
            const payload = cart.map(item => ({
                producto_id: item.id,
                cantidad: item.quantity,
                precioUnitario: item.precio_venta
            }));

            // Single bulk request
            await registrarOperacion('venta', payload);

            setShowSuccessModal(true);
            setTimeout(() => {
                setCart([]);
                setShowSuccessModal(false);
                handleClearSearch(); // Clear products and search after success to keep it clean
            }, 3000);

        } catch (error) {
            console.error("Checkout error:", error);
            alert("Error al procesar la venta. Verifique el stock o la conexión.");
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="pos-container">
            <div className="pos-products-section">
                <div style={{ marginBottom: '15px' }}>
                    <SearchBar
                        key={searchKey}
                        placeholder="Buscar por nombre, código o referencia..."
                        onSearch={handleSearch}
                    />
                </div>

                {isSearching && (
                    <div className="search-results-banner" style={{ marginBottom: '15px' }}>
                        <span className="search-results-text">
                            Resultados para: &quot;{searchQuery}&quot;
                            {pagination.totalItems !== undefined && ` (${pagination.totalItems} encontrados)`}
                        </span>
                        <button
                            className="clear-search-btn"
                            onClick={handleClearSearch}
                        >
                            Limpiar búsqueda
                        </button>
                    </div>
                )}

                <div className="pos-products-grid">
                    {products.length === 0 && !loading && searchQuery === '' && (
                        <div className="pos-empty-state">
                            <p>Use el buscador o escanee un código para comenzar</p>
                        </div>
                    )}
                    {products.map(product => {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const stock = (product as any).Inventario?.stock || (product as any).stock || 0;
                        return (
                            <div
                                key={product.id}
                                className={`product-card ${stock === 0 ? 'no-stock' : ''}`}
                                onClick={() => addToCart(product)}
                            >
                                <div className="product-info">
                                    <h3>{product.nombre}</h3>
                                    <p className="product-details">
                                        {[product.marca, product.modelo, product.referencia].filter(Boolean).join(' - ')}
                                    </p>
                                    <p className="stock">Stock: {stock}</p>
                                    {product.Ubicacion && (
                                        <p className="location" style={{ fontSize: '0.8em', color: '#10b981', marginTop: '4px' }}>
                                            📍 {product.Ubicacion.nombre} (Zona {product.Ubicacion.zona_abc})
                                        </p>
                                    )}
                                </div>
                                <div className="price">${product.precio_venta.toLocaleString()}</div>
                            </div>
                        );
                    })}
                </div>
                
                {isSearching && pagination.totalPages !== undefined && pagination.totalPages > 1 && (
                    <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            currentPage={pagination.currentPage || 1}
                            totalPages={pagination.totalPages || 1}
                            hasNextPage={currentPage < (pagination.totalPages || 1)}
                            hasPrevPage={currentPage > 1}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>

            <div className="pos-cart-section">
                <div className="cart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2>Carrito de Venta</h2>
                    {cart.length > 0 && (
                        <button 
                            className="button secondary" 
                            style={{ padding: '5px 10px', fontSize: '0.9em', backgroundColor: '#f87171', color: 'white', border: 'none', cursor: 'pointer' }}
                            onClick={() => setCart([])}
                            title="Vaciar carrito"
                        >
                            <span style={{ marginRight: '5px' }}>🗑️</span> Limpiar Carrito
                        </button>
                    )}
                </div>

                <div className="cart-items">
                    {cart.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#999', marginTop: '40px' }}>
                            El carrito está vacío
                        </p>
                    ) : (
                        cart.map(item => (
                            <div key={item.id} className="cart-item">
                                <div className="cart-item-info">
                                    <span className="cart-item-title">{item.nombre}</span>
                                    <span className="cart-item-details">
                                        {[item.marca, item.modelo, item.referencia].filter(Boolean).join(' - ')}
                                    </span>
                                    <span className="cart-item-price">
                                        ${item.precio_venta.toLocaleString()} x {item.quantity}
                                    </span>
                                </div>
                                <div className="cart-controls">
                                    <button
                                        className="qty-btn"
                                        onClick={() => updateQuantity(item.id, -1)}
                                    >-</button>
                                    <span>{item.quantity}</span>
                                    <button
                                        className="qty-btn"
                                        onClick={() => updateQuantity(item.id, 1)}
                                    >+</button>
                                    <button
                                        className="remove-btn"
                                        onClick={() => removeFromCart(item.id)}
                                    >✖</button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="cart-footer">
                    <div className="cart-summary">
                        <div className="summary-row">
                            <span>Subtotal</span>
                            <span>${calculateTotal().toLocaleString()}</span>
                        </div>
                        <div className="summary-row total">
                            <span>Total</span>
                            <span>${calculateTotal().toLocaleString()}</span>
                        </div>
                    </div>

                    <button
                        className="pay-btn"
                        disabled={cart.length === 0 || processing}
                        onClick={handleCheckout}
                    >
                        {processing ? 'Procesando...' : `Cobrar $${calculateTotal().toLocaleString()}`}
                    </button>
                </div>
            </div>

            {/* Mock Success Modal */}
            {showSuccessModal && (
                <div className="modal">
                    <div className="modal-content" style={{ textAlign: 'center' }}>
                        <h2 style={{ color: '#10b981', marginBottom: '20px' }}>¡Venta Exitosa!</h2>
                        <div style={{ fontSize: '4em', marginBottom: '20px' }}>✅</div>
                        <p>Imprimiendo factura...</p>
                        <p style={{ fontSize: '0.8em', color: '#666' }}>(Simulación)</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default POS;
